/* eslint-disable @typescript-eslint/no-explicit-any */
"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Download, Filter, Search, ArrowUpDown, Eye, ChevronLeft, ChevronRight } from "lucide-react"
import { Link } from "react-router-dom"
import { DocumentViewPopup } from "@/components/document-view-popup"
import type { Document } from "@/lib/interfaces"
import bgImage from "@/assets/favicon.png"
import apiService from "@/services/apiService"
import { useAppStore } from "@/store/appStore"

interface GDSummaryData {
  id: string
  gdNumber: string
  dateTime: string
  policeStation: string
  officerName: string
  officerRank: string
  gdType: string
  sections: string[]
  caseType: string
  status: "COMPLETE" | "PENDING" | "INCOMPLETE"
  createdAt?: string
}

const formatDate = (value?: string) => (value ? new Date(value).toLocaleDateString("en-IN") : "-")
const formatDateTime = (value?: string) => (value ? new Date(value).toLocaleString("en-IN") : "-")

export default function GDSummaryReport() {
  const { _filterOptions } = useAppStore();
  const [gdSummaryData, setGdSummaryData] = useState<GDSummaryData[]>([])
  const [viewingDocument, setViewingDocument] = useState<Document | null>(null)
  const [filteredData, setFilteredData] = useState(gdSummaryData)
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    policeStation: "",
    officer: "",
    gdType: "",
    status: "",
  })
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" })
  const [searchTerm, setSearchTerm] = useState("")
  const [policeStations, setPoliceStations] = useState([])
  const [gdTypes, setGdTypes] = useState([])
  const [totalPages, setTotalPages] = useState(0)
  const [startIndex, setStartIndex] = useState(0)
  const [endIndex, setEndIndex] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setPoliceStations(_filterOptions?.policeStations || [])
    fetchDocuments();
  }, [])

  const handlePageChange = (value: number) => {
    setCurrentPage(value);
    fetchDocuments(itemsPerPage, value);
  }

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1); // Reset to first page when changing items per page
    fetchDocuments(parseInt(value), 1);
  }

  // Build query parameters for API call
  const buildQueryParams = (limit = 10, page = 1, includeFilters = false) => {
    const params = new URLSearchParams({
      limit: limit.toString(),
      page: page.toString(),
    });

    if (includeFilters) {
      if (filters.dateFrom) {
        params.append('dateFrom', filters.dateFrom);
      }
      if (filters.dateTo) {
        params.append('dateTo', filters.dateTo);
      }
      if (filters.policeStation && filters.policeStation !== "All") {
        params.append('policeStation', filters.policeStation);
      }
      if (filters.officer) {
        params.append('officer', filters.officer);
      }
      if (filters.gdType && filters.gdType !== "All") {
        params.append('gdType', filters.gdType);
      }
      if (filters.status && filters.status !== "All") {
        params.append('status', filters.status);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (sortConfig.key) {
        params.append('sortBy', sortConfig.key);
        params.append('sortOrder', sortConfig.direction);
      }
    }

    return params.toString();
  }

  const fetchDocuments = async (limit = 10, page = 1, includeFilters = false) => {
    setLoading(true);
    try {
      const queryParams = buildQueryParams(limit, page, includeFilters);
      const jsonResponse: any = await apiService.get(`general-diaries?${queryParams}`);
      
      const filteredResponse = jsonResponse.data.map((data: any, index: number) => ({
        id: index,
        gdNumber: data.gdNo,
        dateTime: data.gdDateTime,
        policeStation: data.policeStation,
        officerName: data.officerName,
        officerRank: data.reportPrintedByRank,
        gdType: data.gdType,
        sections: data.actsAndSections.map((actAndSection: any) => actAndSection.section),
        caseType: data.caseType,
        status: "COMPLETE",
        ...data
      }));
      
      setTotalPages(jsonResponse.pagination.totalPages);
      setStartIndex(jsonResponse.pagination.limit * (jsonResponse.pagination.page - 1));
      setEndIndex(Math.min(jsonResponse.pagination.limit * jsonResponse.pagination.page, jsonResponse.pagination.total));
      setCurrentPage(jsonResponse.pagination.page);
      
      // Only update gdTypes if not filtering (initial load)
      if (!includeFilters) {
        const uniqueGdTypes: any = [
          ...new Set(filteredResponse.map((item: GDSummaryData) => item.gdType)),
        ];
        setGdTypes(uniqueGdTypes);
        setGdSummaryData(filteredResponse);
      }
      
      setFilteredData(filteredResponse);
      console.log(jsonResponse.data, 'generalDiaries');
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleSort = (key: string) => {
    let direction = "asc"
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc"
    }
    setSortConfig({ key, direction })

    // Fetch data with new sort order
    fetchDocuments(itemsPerPage, currentPage, true);
  }

  // Updated applyFilters to call API instead of filtering locally
  const applyFilters = () => {
    setCurrentPage(1); // Reset to first page when applying filters
    fetchDocuments(itemsPerPage, 1, true);
  }

  // Clear filters function
  const clearFilters = () => {
    setFilters({
      dateFrom: "",
      dateTo: "",
      policeStation: "",
      officer: "",
      gdType: "",
      status: "",
    });
    setSearchTerm("");
    setSortConfig({ key: "", direction: "asc" });
    setCurrentPage(1);
    fetchDocuments(itemsPerPage, 1, false);
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      COMPLETE: { color: "bg-green-100 text-green-800", text: "Complete" },
      PENDING: { color: "bg-yellow-100 text-yellow-800", text: "Pending" },
      INCOMPLETE: { color: "bg-red-100 text-red-800", text: "Incomplete" },
    }
    const config = statusConfig[status as keyof typeof statusConfig]
    return <Badge className={`${config.color} hover:${config.color}`}>{config.text}</Badge>
  }

  const exportToCSV = () => {
    const headers = [
      "GD Number",
      "GD Date & Time",
      "Upload Date",
      "Police Station",
      "Officer Name",
      "Rank",
      "GD Type",
      "Sections",
      "Case Type",
      "Status",
    ]
    const csvContent = [
      headers.join(","),
      ...filteredData.map((row) =>
        [
          row.gdNumber,
          formatDateTime(row.dateTime),
          formatDate(row.createdAt),
          row.policeStation,
          row.officerName,
          row.officerRank,
          row.gdType,
          row.sections.join("; "),
          row.caseType,
          row.status,
        ].join(","),
      ),
    ].join("\n")

    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `gd-summary-${new Date().toISOString().split("T")[0]}.csv`
    a.click()
  }

  const handleViewDocument = (document: any) => {
    setViewingDocument(document)
  }

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#0d2846]/3 via-[#e2af58]/10 to-[#0d2846]/3 p-6">
      <div
        className="absolute inset-0 bg-no-repeat bg-center bg-contain opacity-3 pointer-events-none"
        style={{
          backgroundImage: `url(${bgImage})`,
        }}
      />
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <Link to="/reports" className="text-blue-600 hover:text-blue-800 text-sm mb-2 inline-block">
              ← Back to Reports
            </Link>
            <h1 className="text-3xl font-bold text-black">General Diary Summary Report</h1>
            <p className="text-gray-600 mt-1">Overview of all GD entries with filtering and sorting options</p>
          </div>
          <Button onClick={exportToCSV} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-black">{filteredData.length}</div>
              <p className="text-sm text-gray-600">Total GD Entries</p>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-600">
                {filteredData.filter((item) => item.status === "COMPLETE").length}
              </div>
              <p className="text-sm text-gray-600">Complete</p>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-yellow-600">
                {filteredData.filter((item) => item.status === "PENDING").length}
              </div>
              <p className="text-sm text-gray-600">Pending</p>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-600">
                {filteredData.filter((item) => item.status === "INCOMPLETE").length}
              </div>
              <p className="text-sm text-gray-600">Incomplete</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg text-black flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters & Search
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">From Date</Label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  className="border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">To Date</Label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  className="border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Police Station</Label>
                <Select
                  value={filters.policeStation}
                  onValueChange={(value) => setFilters({ ...filters, policeStation: value })}
                >
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Select station" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    <SelectItem value="All">All Stations</SelectItem>
                    {policeStations.map((station: any, index: number) => (
                      <SelectItem key={station.name + index} value={station.name}>
                        {station.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">GD Type</Label>
                <Select value={filters.gdType} onValueChange={(value) => setFilters({ ...filters, gdType: value })}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    <SelectItem value="All">All Types</SelectItem>
                    {gdTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search GD Number, Officer, Station, Case Type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-gray-300"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={applyFilters} 
                  disabled={loading}
                  className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white"
                >
                  {loading ? "Applying..." : "Apply Filters"}
                </Button>
                <Button 
                  onClick={clearFilters} 
                  variant="outline"
                  disabled={loading}
                  className="border-gray-300"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card className="border border-gray-200">
          <CardHeader>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <CardTitle className="text-lg text-black">
                  GD Entries ({filteredData.length} records)
                  {loading && <span className="text-sm text-gray-500 ml-2">Loading...</span>}
                </CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Show:</span>
                <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-gray-600">per page</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-black font-semibold">
                      <Button
                        variant="ghost"
                        onClick={() => handleSort("gdNumber")}
                        className="p-0 h-auto font-semibold"
                        disabled={loading}
                      >
                        GD Number <ArrowUpDown className="ml-1 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-black font-semibold">
                      <Button
                        variant="ghost"
                        onClick={() => handleSort("dateTime")}
                        className="p-0 h-auto font-semibold"
                        disabled={loading}
                      >
                        GD Date & Time <ArrowUpDown className="ml-1 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-black font-semibold">Upload Date</TableHead>
                    <TableHead className="text-black font-semibold">
                      <Button
                        variant="ghost"
                        onClick={() => handleSort("policeStation")}
                        className="p-0 h-auto font-semibold"
                        disabled={loading}
                      >
                        Police Station <ArrowUpDown className="ml-1 h-4 w-4" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-black font-semibold">Officer Name & Rank</TableHead>
                    <TableHead className="text-black font-semibold">Case Type</TableHead>
                    <TableHead className="text-black font-semibold">Status</TableHead>
                    <TableHead className="text-black font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                          Loading...
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : filteredData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        No GD entries found matching your criteria.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredData.map((item: any) => (
                      <TableRow key={item.id} className="hover:bg-gray-50">
                        <TableCell className="font-medium text-black">{item.gdNumber}</TableCell>
                        <TableCell className="text-gray-600">{formatDateTime(item.dateTime)}</TableCell>
                        <TableCell className="text-gray-600">{formatDate(item.createdAt)}</TableCell>
                        <TableCell className="text-gray-600">{item.policeStation?.name}</TableCell>
                        <TableCell className="text-gray-600">
                          <div>
                            <div className="font-medium">{item.officerName}</div>
                            <div className="text-sm text-gray-500">{item.officerRank}</div>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-600">{item.caseType}</TableCell>
                        <TableCell>{getStatusBadge(item.status)}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" className="hover:bg-gray-100" onClick={() => handleViewDocument({ generalDiary: item })}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            {/* Pagination */}
            {totalPages > 1 && !loading && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-gray-600">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredData.length)} of {filteredData.length} entries
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="border-gray-300"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((page) => {
                        // Show first page, last page, current page, and pages around current
                        return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1
                      })
                      .map((page, index, array) => (
                        <div key={page} className="flex items-center">
                          {index > 0 && array[index - 1] !== page - 1 && (
                            <span className="px-2 text-gray-400">...</span>
                          )}
                          <Button
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(page)}
                            className={currentPage === page ? "bg-black text-white" : "border-gray-300"}
                          >
                            {page}
                          </Button>
                        </div>
                      ))}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="border-gray-300"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      {/* Document View Popup */}
      <DocumentViewPopup
        open={!!viewingDocument}
        onOpenChange={() => setViewingDocument(null)}
        document={viewingDocument}
      />
    </div>
  )
}